/**
 * Photo field: pick, resize, preview, upload, remove.
 *
 * The picked file is squared off and shrunk to a 512 px WebP here before
 * anything is sent (lib/resize-image) — photos are stored in D1, so what
 * leaves the browser has to be a few tens of kilobytes rather than the several
 * megabytes a phone camera hands over. The preview shows the resized result,
 * which is what the ring will actually draw.
 */
import { MAX_IMAGE_BYTES, imageUrl, type UploadedImage } from '../app/api';
import { MAX_SOURCE_BYTES, resizeImage } from '../lib/resize-image';
import { el } from './dom';
import { errorMessage, toast } from './toast';

const ACCEPTED = ['image/jpeg', 'image/png', 'image/webp'];

/** Said only when the server had nothing better to say. */
export const UPLOAD_FAILED = 'Couldn’t upload the photo. Try again.';

export interface PhotoPickerOptions {
  /** Existing image key, when editing. */
  imageKey?: string | undefined;
  /** Does the upload; differs between the owner API and the collect form. */
  upload: (file: File) => Promise<UploadedImage>;
  label?: string;
  onChange?: (key: string | null) => void;
}

export class PhotoPicker {
  readonly root: HTMLElement;
  private key: string | null;
  private objectUrl: string | undefined;
  private readonly preview: HTMLElement;
  private readonly input: HTMLInputElement;
  private readonly choose: HTMLButtonElement;
  private readonly remove: HTMLButtonElement;
  private readonly status: HTMLElement;
  private readonly options: PhotoPickerOptions;

  constructor(options: PhotoPickerOptions) {
    this.options = options;
    this.key = options.imageKey ?? null;

    this.preview = el('div', { class: 'photo__preview photo__preview--empty', 'aria-hidden': 'true' });
    this.input = el('input', {
      type: 'file',
      accept: ACCEPTED.join(','),
      onChange: () => void this.onPick(),
    });
    this.choose = el('button', {
      class: 'btn btn--small',
      type: 'button',
      text: options.imageKey ? 'Replace photo' : 'Add photo',
      onClick: () => this.input.click(),
    });
    this.remove = el('button', {
      class: 'btn btn--small btn--quiet',
      type: 'button',
      text: 'Remove',
      onClick: () => this.set(null),
    });
    this.status = el('div', {
      class: 'field__hint',
      text: 'JPEG, PNG or WebP. It is shrunk to a small square here before it is sent.',
    });

    this.root = el(
      'div',
      { class: 'field' },
      el('span', { class: 'field__label muted small', text: options.label ?? 'Photo' }),
      el(
        'div',
        { class: 'photo' },
        this.preview,
        el('div', { class: 'stack' }, el('div', { class: 'row' }, this.choose, this.remove), this.status),
        this.input,
      ),
    );

    this.paint();
  }

  /** null when there is no photo; the key is what a create/patch body wants. */
  get value(): string | null {
    return this.key;
  }

  set(key: string | null): void {
    this.key = key;
    this.releaseObjectUrl();
    this.paint();
    this.options.onChange?.(key);
  }

  destroy(): void {
    this.releaseObjectUrl();
  }

  private releaseObjectUrl(): void {
    if (this.objectUrl) URL.revokeObjectURL(this.objectUrl);
    this.objectUrl = undefined;
  }

  private paint(src?: string): void {
    this.preview.replaceChildren();
    const url = src ?? (this.key ? imageUrl(this.key) : undefined);
    if (url) {
      this.preview.className = 'photo__preview';
      this.preview.style.backgroundImage = `url(${JSON.stringify(url)})`;
      this.preview.style.backgroundSize = 'cover';
      this.preview.style.backgroundPosition = 'center';
    } else {
      this.preview.className = 'photo__preview photo__preview--empty';
      this.preview.style.backgroundImage = '';
      this.preview.textContent = '＋';
    }
    this.remove.hidden = !this.key;
    this.choose.textContent = this.key ? 'Replace photo' : 'Add photo';
  }

  private async onPick(): Promise<void> {
    const file = this.input.files?.[0];
    this.input.value = '';
    if (!file) return;

    if (!ACCEPTED.includes(file.type)) {
      this.status.textContent = 'That file type is not supported. Use JPEG, PNG or WebP.';
      return;
    }
    if (file.size > MAX_SOURCE_BYTES) {
      this.status.textContent = `That photo is ${(file.size / 1024 / 1024).toFixed(0)} MB, which is more than this can open.`;
      return;
    }

    this.status.textContent = 'Preparing…';
    this.choose.disabled = true;
    try {
      const resized = await resizeImage(file);
      if (resized.bytes > MAX_IMAGE_BYTES) {
        // The ladder ran out of steps. Saying so beats starting an upload the
        // worker is certain to refuse.
        throw new Error('That photo could not be made small enough. Try a different one.');
      }
      this.releaseObjectUrl();
      this.objectUrl = URL.createObjectURL(resized.file);
      this.paint(this.objectUrl);
      this.status.textContent = 'Uploading…';
      const stored = await this.options.upload(resized.file);
      this.key = stored.key;
      this.status.textContent = 'Photo ready.';
      this.options.onChange?.(stored.key);
    } catch (err) {
      // Whatever the server said — that the calendar is at its photo limit,
      // that uploads are paused for today — is more use than a generic apology;
      // the fallback only covers the case where it said nothing worth
      // repeating. A failure in the resize itself arrives here as an Error with
      // its own sentence.
      const message = errorMessage(err, UPLOAD_FAILED);
      toast(message, 'error', 7000);
      this.status.textContent = message;
      this.releaseObjectUrl();
      this.paint();
    } finally {
      this.choose.disabled = false;
      this.remove.hidden = !this.key;
    }
  }
}
